import { SyntheticEvent, useEffect, useState } from 'react'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import cn from 'classnames'
import { MODAL_IDS, ModalItem, ModalSimple, Seo, useModal } from 'components'
import { useDebounce, useOnMount } from 'hooks'
import { useDeleteContactMutation, useGetContactListQuery } from 'modules/contact/api'
import { Table } from 'modules/contact/components'
import { getContactFullName } from 'modules/contact/helpers'
import { TContact } from 'modules/contact/types'
import { Button } from 'src/shared/components'

import { contactCreateRoute, contactDetailRoute, contactDetailUpdateRoute, contactRoute } from '../../routes'

import s from './styles.module.less'
import cs from 'src/common.module.less'

const ContactList = () => {
  const title = 'контакт'
  const subtitle = 'контактом'
  const {
    location: { pathname }
  } = useRouterState()
  const navigate = useNavigate()
  const { openModal, closeModal } = useModal()
  const [focusedContact, setFocusedContact] = useState<TContact | null>(null)

  const [deleteContact, { isLoading: isDeleting }] = useDeleteContactMutation()

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const searchParams = contactRoute.useSearch()
  const debouncedSearchContact = useDebounce(debouncedSearchTerm, 500)
  const [filteredSearchTerm, setFilteredSearchTerm] = useState(debouncedSearchContact)
  const { data: contact, isFetching: isContactFetching, refetch } = useGetContactListQuery(filteredSearchTerm)

  const searchQuery = searchParams.search || ''

  const handleSearchContact = (search: string) => {
    setDebouncedSearchTerm(search)
    navigate({
      from: contactRoute.to,
      search: {
        search
      }
    })

    if (search.length === 0) {
      navigate({
        from: contactRoute.to,
        search: {
          search: ''
        }
      })
    }
  }

  useEffect(() => {
    if (debouncedSearchContact.length === 0 || debouncedSearchContact.length > 1) {
      setFilteredSearchTerm(searchQuery)
    }
  }, [debouncedSearchContact, searchQuery])

  const handleOpenModalDetail = (contact: TContact) => {
    navigate({ to: contactDetailRoute.to, params: { id: contact.id } })
  }

  const handleOpenModalDelete = (e: SyntheticEvent, contact: TContact) => {
    e.stopPropagation()
    setFocusedContact(contact)
    openModal(MODAL_IDS.DeleteContact)
  }

  const handleDelete = () => {
    if (focusedContact) {
      deleteContact(focusedContact.id).finally(() => {
        closeModal(MODAL_IDS.DeleteContact)
        refetch()
      })
    }
  }

  const handleOpenContactAdd = () => {
    navigate(contactCreateRoute)
  }

  const handleUpdateContact = (e: SyntheticEvent, contact: TContact | undefined) => {
    e.stopPropagation()
    if (contact) {
      setFocusedContact(contact)
    }
    if (!contact?.id) return
    navigate({ to: contactDetailUpdateRoute.to, params: { id: contact?.id }, search: { from: pathname, skip: true } })
  }

  useOnMount(() => {
    refetch()
  })

  return (
    <>
      <Seo
        description="Справочник грузоотправителей и грузополучателей, которые указываются при создании заявки в сервисе QP5 Express."
        title="Мои контакты – сборные перевозки QP5 Express"
      />
      <ModalItem id={MODAL_IDS.DeleteContact}>
        <ModalSimple
          id={MODAL_IDS.DeleteContact}
          onClose={() => closeModal(MODAL_IDS.DeleteContact)}
          title={`Удалить ${title} «${focusedContact ? getContactFullName(focusedContact) : '-'}»?`}
          mobileModalFooter={
            <Button
              className="block w-full"
              disabled={!focusedContact?.access.delete}
              loading={isDeleting}
              onClick={handleDelete}
              size="lg"
              variant="solid"
            >
              Удалить
            </Button>
          }
          modalFooter={
            <div className="ml-auto flex gap-x-2">
              <Button
                className="ml-auto"
                onClick={() => closeModal(MODAL_IDS.DeleteContact)}
                size="lg"
                variant="outline"
              >
                Не удалять
              </Button>
              <Button
                disabled={!focusedContact?.access.delete}
                loading={isDeleting}
                onClick={handleDelete}
                size="lg"
                variant="solid"
              >
                Удалить
              </Button>
            </div>
          }
        >
          {`В заявках, которые были ранее созданы с этим ${subtitle}, информация о нём сохранится.`}
        </ModalSimple>
      </ModalItem>
      <Outlet />
      <div className={cn(cs.container, s.container)}>
        <Table
          contact={contact}
          deleteContact={deleteContact}
          handleOpenContactAdd={handleOpenContactAdd}
          handleOpenModalDelete={handleOpenModalDelete}
          handleOpenModalDetail={handleOpenModalDetail}
          handleSearchContact={handleSearchContact}
          handleUpdateContact={handleUpdateContact}
          isLoading={isContactFetching}
          refetch={refetch}
          searchQuery={searchQuery}
        />
      </div>
    </>
  )
}

export default ContactList
